import { getZodiacFromSolarDate, buildLunarString } from '../utils/lunarCalc';
import type { Profile } from '../pages/profileManage/types';
import { multiLanguageHeader } from '@/utils/languageHeader';

export interface MingliProfileVO {
  id: number;
  name: string;
  gender: number;
  tag: string;
  isCurrent: number;
  avatar: string;
  birthTime: number;
  birthPlace: string;
  sortNo: number;
  timezone: string;
  birthLongitude: number;
  birthLatitude: number;
}

export interface SaveProfileInput {
  id?: number;
  name: string;
  gender: 'male' | 'female';
  tag: string;
  isCurrent: boolean;
  avatar: string;
  birthTime: string;
  birthPlace?: string;
  timezone?: string;
  birthLongitude?: number;
  birthLatitude?: number;
  sortNo?: number;
}

/** 服务端保存负载：gender/isCurrent/birthTime 均为数值（与 VO 一致） */
export interface MingliProfileSavePayload {
  id?: number;
  name: string;
  gender: number;
  tag: string;
  isCurrent: number;
  avatar: string;
  birthTime: number;
  birthPlace?: string;
  timezone?: string;
  birthLongitude?: number;
  birthLatitude?: number;
  sortNo?: number;
}

const PROFILE_BASE = (import.meta.env.VITE_API_BASE as string || '') + '/api/mingli/profile';

async function request<T>(url: string, options?: Parameters<typeof fetch>[1]): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: { ...multiLanguageHeader(), ...(options?.headers as Record<string, string>) },
  });
  const json = await res.json().catch(() => null);
  if (json && json.status === 200) return json.data as T;
  throw new Error(json?.message || `请求失败（HTTP ${res.status}）`);
}

export async function fetchProfiles(): Promise<MingliProfileVO[]> {
  const data = await request<{ profiles: MingliProfileVO[] }>(`${PROFILE_BASE}/list`);
  return data.profiles ?? [];
}

export async function saveProfile(input: SaveProfileInput): Promise<MingliProfileVO> {
  const data = await request<{ profile: MingliProfileVO }>(`${PROFILE_BASE}/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(profileToSave(input)),
  });
  return data.profile;
}

export async function deleteProfile(id: number): Promise<void> {
  await request<void>(`${PROFILE_BASE}/delete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  });
}

export async function deleteProfiles(ids: number[]): Promise<void> {
  await request<void>(`${PROFILE_BASE}/deleteBatch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
}

export async function setCurrentProfile(id: number): Promise<void> {
  await request<void>(`${PROFILE_BASE}/setCurrent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id }),
  });
}

/** 毫秒时间戳 → "YYYY-MM-DD HH:mm"（固定按 UTC+8 东八区墙钟时间，命主出生地为中国大陆） */
function tsToBirthTime(ts: number): string {
  const d = new Date(ts + 8 * 3600 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** "YYYY-MM-DD HH:mm" → 毫秒时间戳（固定按 UTC+8 东八区墙钟时间） */
function birthTimeToTs(birthTime: string): number {
  const [datePart, timePart = '00:00'] = birthTime.trim().split(' ');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  return Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0) - 8 * 3600 * 1000;
}

function deriveSolar(birthTime: string): string {
  return birthTime
    ? `${birthTime.replace('-', '年').replace('-', '月').replace(' ', '日 ')}時`
    : '';
}

export function normalizeBirthPlace(birthPlace: string): string {
  return birthPlace.replace(/#+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** 服务端 VO → 前端 Profile（补全 solar/zodiac/lunar） */
export function voToProfile(vo: MingliProfileVO): Profile {
  const birthTime = tsToBirthTime(vo.birthTime);
  let zodiac = '';
  let lunar = '';
  if (birthTime) {
    try {
      const [datePart, timePart] = birthTime.split(' ');
      const [y, m, d] = datePart.split('-').map(Number);
      const hour = timePart ? parseInt(timePart.split(':')[0], 10) : 0;
      zodiac = getZodiacFromSolarDate(new Date(y, m - 1, d));
      lunar = buildLunarString(new Date(y, m - 1, d), hour);
    } catch { /* keep empty */ }
  }
  return {
    id: vo.id,
    name: vo.name,
    gender: vo.gender === 2 ? 'female' : 'male',
    zodiac: zodiac || '蛇',
    tag: vo.tag || '自己',
    isCurrent: vo.isCurrent === 1,
    isExample: false,
    solar: deriveSolar(birthTime),
    lunar,
    avatar: vo.avatar || '/images/profile/profile-new-001.jpg',
    birthTime,
    birthPlace: normalizeBirthPlace(vo.birthPlace),
    calendarType: 'solar',
  };
}

/** 前端 Profile → 服务端保存负载（MingliProfileSavePayload，数值型） */
export function profileToSave(p: {
  id?: number; name: string; gender: 'male' | 'female'; isCurrent: boolean;
  avatar: string; birthTime?: string; birthPlace?: string; tag?: string;
  zodiac?: string; solar?: string; lunar?: string; isExample?: boolean;
  timezone?: string; birthLongitude?: number; birthLatitude?: number; sortNo?: number;
}): MingliProfileSavePayload {
  return {
    id: p.id,
    name: p.name,
    gender: p.gender === 'female' ? 2 : 1,
    tag: p.tag || '自己',
    isCurrent: p.isCurrent ? 1 : 0,
    avatar: p.avatar || '',
    birthTime: p.birthTime ? birthTimeToTs(p.birthTime) : 0,
    birthPlace: p.birthPlace,
    ...(p.timezone !== undefined ? { timezone: p.timezone } : {}),
    ...(p.birthLongitude !== undefined ? { birthLongitude: p.birthLongitude } : {}),
    ...(p.birthLatitude !== undefined ? { birthLatitude: p.birthLatitude } : {}),
    ...(p.sortNo !== undefined ? { sortNo: p.sortNo } : {}),
  };
}
